/* GASSISA
 =========================================================================================
 * Descripcion: Emision de bytes ESC/POS del ticket de ventas.
 *
 *              El ticket se arma igual que el metodo original
 *              handleImprimirTicketEscapeCommand: comandos ESC, texto y saltos
 *              de linea. La X de cada objeto se manda con ESC $. La Y no se usa
 *              para posicionar; solo agrupa objetos en el mismo renglon.
 *
 *              PrinterTools acepta hasta 96 caracteres por linea (incluyendo
 *              los ESC). Si un renglon no cabe, se continua en otra linea
 *              conservando la X del objeto; no se aplica ancho de campo.
 *
 * Historial de cambios:
 * ---------------------------------------------------------------------------------------
 *    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
 * ---------------------------------------------------------------------------------------
 *      1       | 20/10/2026 | Lic. Jose Luis Aragon Cervantes  | Creacion
 *      2       | 21/10/2026 | Lic. Jose Luis Aragon Cervantes  | Salida directa por esquema
 * ---------------------------------------------------------------------------------------*/

import {
    DOTS_POR_MM,
    MAX_COLUMNAS_LINEA,
    MAX_BYTE_SEGURO,
    normalizarFuenteTipo,
    multiplicadorFuenteTipo,
} from "./ticketPerfil";

export const RESET_SECCION = "\x1D\x21\x00\x1B\x21\x00";
export const RESET_ESTADO_ESTILOS = RESET_SECCION + "\x1B\x45\x00" + "\x1B\x61\x00" + "\x1B\x4D\x00";
const CODIGO_ALINEACION = { left: 0, center: 1, right: 2 };

const construirComandosSeccion = (seccion) => {
    const tipo = normalizarFuenteTipo(seccion?.fuenteTipo);
    const comandos = ["\x1B\x61" + String.fromCharCode(CODIGO_ALINEACION[seccion?.alineacion] ?? 1)];
    if (tipo === "B") comandos.push("\x1B\x4D\x01");
    if (tipo === "D") comandos.push("\x1B\x21" + String.fromCharCode(0x30));
    if (seccion?.negrita) comandos.push("\x1B\x45\x01");
    return {
        comandos,
        requiereReset: tipo === "B" || tipo === "D" || Boolean(seccion?.negrita),
    };
};

export const comandoSeccion = (textEncoder, seccion, texto) => {
    const { comandos, requiereReset } = construirComandosSeccion(seccion);
    const textoLimpio = String(texto ?? "")
        .split("\n")
        .map((linea) => linea.trim())
        .join("\n")
        .trim();

    return {
        bytes: textEncoder.encode(comandos.join("") + textoLimpio + "\n"),
        multilinea: textoLimpio.includes("\n"),
        requiereReset,
    };
};

export const encodeTicketDatoClave = (textEncoder, texto) => (
    textEncoder.encode("\x1B\x45\x01\x1B\x21\x10" + texto + "\x1B\x21\x00\x1B\x45\x00")
);

// Emite una linea de fallback completa. Si el texto excede el limite de
// transporte de PrinterTools, se continua en la linea siguiente sin perder
// caracteres; el prefijo ESC solo se envia en el primer bloque.
const emitirLineaCompleta = (textEncoder, prefijo, texto, sufijo = "") => {
    const limpio = String(texto ?? "");
    const partes = [];

    if (limpio.length === 0) {
        partes.push(textEncoder.encode(prefijo + sufijo + "\n"));
        return partes;
    }

    let inicio = 0;
    let primera = true;

    while (inicio < limpio.length) {
        const prefijoLinea = primera ? prefijo : "";
        const maximo = Math.max(1, MAX_COLUMNAS_LINEA - prefijoLinea.length);
        const restante = limpio.length - inicio;
        let tamano;
        if (restante + sufijo.length <= maximo) {
            tamano = restante;
        } else if (restante > maximo) {
            tamano = maximo;
        } else {
            tamano = Math.max(1, maximo - sufijo.length);
        }
        const bloque = limpio.slice(inicio, inicio + tamano);
        const esUltimo = inicio + tamano >= limpio.length;

        partes.push(textEncoder.encode(prefijoLinea + bloque + (esUltimo ? sufijo : "") + "\n"));
        primera = false;
        inicio += tamano;
    }

    return partes;
};

const byteSeguro = (valor, contexto) => {
    const numero = Math.round(valor);
    if (numero < 0 || numero > MAX_BYTE_SEGURO) {
        throw new Error(`Valor de comando fuera de rango seguro (${contexto}): ${numero}`);
    }
    return numero;
};

// ESC $ nL nH  (posicion horizontal absoluta en dots)
export const comandoPosicionX = (xDots) => {
    const objetivo = Math.max(0, Math.min(32767, Math.round(xDots)));
    if (objetivo === 0) return "";
    const bajo = objetivo % 256;
    const base = bajo <= MAX_BYTE_SEGURO ? objetivo : objetivo - bajo + MAX_BYTE_SEGURO;
    let comandos = "\x1B\x24" + String.fromCharCode(byteSeguro(base % 256, "ESC $ xL"), byteSeguro(Math.floor(base / 256), "ESC $ xH"));
    let extra = objetivo - base;
    while (extra > 0) {
        const chunk = Math.min(extra, MAX_BYTE_SEGURO);
        comandos += "\x1B\x5C" + String.fromCharCode(chunk, 0);
        extra -= chunk;
    }
    return comandos;
};

const comandosEstiloObjeto = (objeto, estado) => {
    const comandos = [];
    const tipo = normalizarFuenteTipo(objeto.fuenteTipo);
    const fuente = tipo === "B" ? 1 : 0;
    const bits = tipo === "D" ? 0x30 : 0;
    const negrita = objeto.negrita ? 1 : 0;

    if (estado.fuente !== fuente) {
        comandos.push("\x1B\x4D" + String.fromCharCode(fuente));
        estado.fuente = fuente;
    }
    if (estado.bits !== bits) {
        comandos.push("\x1B\x21" + String.fromCharCode(bits));
        estado.bits = bits;
    }
    if (estado.negrita !== negrita) {
        comandos.push("\x1B\x45" + String.fromCharCode(negrita));
        estado.negrita = negrita;
    }

    return comandos.join("");
};

const lineasDeTexto = (texto) => {
    const limpio = String(texto ?? "")
        .split("\n")
        .map((linea) => linea.trim())
        .join("\n")
        .trim();
    return limpio.length > 0 ? limpio.split("\n") : [];
};

// La alineacion de labels y campos es dentro de su propio ancho (igual que el
// disenador): no se manda ESC a, se desplaza la X del texto.
export const calcularXDotsObjeto = (
    objeto,
    texto,
    dotsPorMm = DOTS_POR_MM,
    anchoCaracterDots = 9
) => {
    const xBase = (Number(objeto?.xMm) || 0) * dotsPorMm;
    if (objeto?.alineacion !== "right" && objeto?.alineacion !== "center") {
        return xBase;
    }

    const anchoCajaDots = (Number(objeto?.anchoMm) || 0) * dotsPorMm;
    const anchoTextoDots = String(texto ?? "").length
        * Math.max(1, Number(anchoCaracterDots) || 9)
        * multiplicadorFuenteTipo(objeto?.fuenteTipo);
    const disponible = anchoCajaDots - anchoTextoDots;
    if (disponible <= 0) return xBase;

    return xBase + (objeto.alineacion === "center" ? disponible / 2 : disponible);
};

// Emite las filas de una seccion. Cada objeto conserva su X; si la linea
// completa no cabe en el limite de PrinterTools, se parte en continuaciones
// con la misma X. No hay recorte por ancho de campo.
export const renderFilasEsquema = ({
    textEncoder,
    filas,
    dotsPorMm = DOTS_POR_MM,
    anchoCaracterDots = 9,
}) => {
    // Reset de alineacion: el encabezado pudo dejar ESC a 1/2 y los objetos
    // con X=0 no deben heredarla.
    const partes = [textEncoder.encode("\x1B\x61\x00")];
    const estado = { fuente: 0, bits: 0, negrita: 0 };
    let emitio = false;

    const emitir = (linea) => {
        partes.push(textEncoder.encode(linea + "\n"));
        emitio = true;
    };

    (filas || []).forEach((fila) => {
        const items = (fila.items || []).map(({ objeto, texto }) => ({
            objeto,
            lineas: lineasDeTexto(texto),
        }));
        const maxLineas = Math.max(0, ...items.map((item) => item.lineas.length));

        for (let k = 0; k < maxLineas; k++) {
            const activos = items.filter((item) => item.lineas[k] !== undefined);
            let actual = "";

            activos.forEach((item) => {
                const texto = item.lineas[k];
                if (!texto) return;

                const estilo = comandosEstiloObjeto(item.objeto, estado);
                const xcmd = comandoPosicionX(calcularXDotsObjeto(item.objeto, texto, dotsPorMm, anchoCaracterDots));
                const prefijo = estilo + xcmd;

                if (actual.length + prefijo.length + texto.length <= MAX_COLUMNAS_LINEA) {
                    actual += prefijo + texto;
                    return;
                }

                if (actual.length > 0) {
                    emitir(actual);
                    actual = "";
                }

                if (prefijo.length + texto.length <= MAX_COLUMNAS_LINEA) {
                    actual = prefijo + texto;
                    return;
                }

                const disponible = Math.max(1, MAX_COLUMNAS_LINEA - prefijo.length);
                let inicio = 0;
                let primera = true;
                while (inicio < texto.length) {
                    const bloque = texto.slice(inicio, inicio + disponible);
                    emitir((primera ? prefijo : xcmd) + bloque);
                    primera = false;
                    inicio += disponible;
                }
            });

            if (actual.length > 0) emitir(actual);
        }
    });

    if (emitio) {
        partes.push(textEncoder.encode(RESET_ESTADO_ESTILOS));
    }

    return partes;
};

const PREFIJO_DATO_CLAVE = "\x1B\x45\x01\x1B\x21\x10";
const SUFIJO_DATO_CLAVE = "\x1B\x21\x00\x1B\x45\x00";

export const datosFallback = (textEncoder, cabecero, opciones = {}) => {
    const etiquetaFolio = opciones.etiquetaFolio ?? "Remision";
    const lineas = [
        ...emitirLineaCompleta(textEncoder, "\x1B\x61\x00", "Caja: " + (cabecero.pc || "")),
        ...emitirLineaCompleta(textEncoder, PREFIJO_DATO_CLAVE, `${etiquetaFolio}: ${cabecero.remision || ""}`, SUFIJO_DATO_CLAVE),
        ...emitirLineaCompleta(textEncoder, "\x1B\x61\x00", "Vendedor: " + (cabecero.usuario || "")),
        ...emitirLineaCompleta(textEncoder, PREFIJO_DATO_CLAVE, "Fecha: " + (cabecero.fechaventa || ""), SUFIJO_DATO_CLAVE),
        ...emitirLineaCompleta(textEncoder, "\x1B\x61\x00", "Tipo de Pago: " + (cabecero.tipopago || "")),
        ...emitirLineaCompleta(textEncoder, PREFIJO_DATO_CLAVE, "Cliente: " + (cabecero.razonsocial || "No especificado"), SUFIJO_DATO_CLAVE),
    ];

    if (cabecero.concepto) {
        lineas.push(...emitirLineaCompleta(textEncoder, "\x1B\x61\x00", "Concepto: " + cabecero.concepto));
    }
    if (cabecero.grupo) {
        lineas.push(...emitirLineaCompleta(textEncoder, "\x1B\x61\x00", "Grupo: " + cabecero.grupo));
    }

    return lineas;
};

export const detalleFallback = (textEncoder, articulo, opciones = {}) => ([
    ...emitirLineaCompleta(textEncoder, "\x1B\x45\x01", articulo.descripcion || "Sin descripcion", "\x1B\x45\x00"),
    ...emitirLineaCompleta(textEncoder, "\x1B\x61\x00", "Cantidad: " + (articulo.cantidad || 0) + " - Talla: " + (articulo.medida || 0)),
    ...emitirLineaCompleta(textEncoder, "\x1B\x61\x00", "P.Unit: $" + (articulo.costounitario || 0).toFixed(2) + "  Desc: $" + (articulo.descuento || 0).toFixed(2)),
    ...emitirLineaCompleta(textEncoder, "\x1B\x61\x00", "Importe: $" + (articulo.total || 0).toFixed(2)),
    ...(opciones.tipoDocumento === "APARTADO" && articulo.abono !== undefined
        ? emitirLineaCompleta(textEncoder, "\x1B\x61\x00", "Abono: $" + (articulo.abono || 0).toFixed(2))
        : []),
    ...(opciones.tipoDocumento === "APARTADO" && articulo.ivaabono !== undefined
        ? emitirLineaCompleta(textEncoder, "\x1B\x61\x00", "IVA abono: $" + (articulo.ivaabono || 0).toFixed(2))
        : []),
    ...emitirLineaCompleta(textEncoder, "\x1B\x61\x00", "Vendedor: " + ((articulo.vendedorpreventa || "").trim() || "N/A")),
    ...emitirLineaCompleta(textEncoder, "\x1B\x61\x00", "Origen: " + ((articulo.almacenexhibidor || "").trim() || "N/A")),
]);

export const totalesFallback = (textEncoder, cabecero, opciones = {}) => {
    if (opciones.tipoDocumento === "APARTADO") {
        return [
            textEncoder.encode("\x1B\x61\x00Subtotal abonos: "),
            textEncoder.encode("\x1B\x61\x02$" + (cabecero.subtotal || 0).toFixed(2) + "\n"),
            textEncoder.encode("\x1B\x61\x00IVA abonos: "),
            textEncoder.encode("\x1B\x61\x02$" + (cabecero.iva || 0).toFixed(2) + "\n"),
            textEncoder.encode("\x1B\x61\x00Total articulos: "),
            textEncoder.encode("\x1B\x61\x02$" + (cabecero.totalarticulos || 0).toFixed(2) + "\n"),
            textEncoder.encode("\x1B\x61\x00Total abonado: "),
            textEncoder.encode("\x1B\x61\x02$" + (cabecero.totalabonado ?? cabecero.total ?? 0).toFixed(2) + "\n"),
            textEncoder.encode("\x1B\x61\x00Restante: "),
            textEncoder.encode("\x1B\x61\x02$" + (cabecero.restante || 0).toFixed(2) + "\n"),
            ...(String(cabecero.montoletra ?? cabecero.montoabonoletra ?? "").trim()
                ? [textEncoder.encode("Total en letra:\n" + (cabecero.montoletra ?? cabecero.montoabonoletra) + "\n")]
                : []),
        ];
    }

    const folioVale = Number(cabecero.foliovaleporcambio ?? cabecero.FolioValePorCambio ?? cabecero.foliovale ?? 0) || 0;
    const montoVale = Number(cabecero.montovaleporcambio ?? cabecero.MontoValePorCambio ?? cabecero.montovale ?? 0) || 0;
    const hayVale = folioVale > 0 && montoVale > 0;

    const partes = [
        textEncoder.encode("\x1B\x61\x00Subtotal: "),
        textEncoder.encode("\x1B\x61\x02$" + (cabecero.subtotal || 0).toFixed(2) + "\n"),
        textEncoder.encode("\x1B\x61\x00IVA: "),
        textEncoder.encode("\x1B\x61\x02$" + (cabecero.iva || 0).toFixed(2) + "\n"),
    ];

    if (hayVale) {
        partes.push(
            textEncoder.encode("\x1B\x61\x00Folio Vale: "),
            textEncoder.encode("\x1B\x61\x02" + folioVale + "\n"),
            textEncoder.encode("\x1B\x61\x00Descto Vale: "),
            textEncoder.encode("\x1B\x61\x02$" + montoVale.toFixed(2) + "\n")
        );
    }

    partes.push(
        textEncoder.encode("\x1B\x61\x00Total: "),
        textEncoder.encode("\x1B\x61\x02$" + (cabecero.total || 0).toFixed(2) + "\n")
    );

    if ((cabecero.tipopago || "").trim() !== "EFECTIVO") {
        partes.push(
            textEncoder.encode("\x1B\x61\x00Total tipo pago: "),
            textEncoder.encode("\x1B\x61\x02$" + (cabecero.totaltipopago || 0).toFixed(2) + "\n")
        );
    }

    partes.push(
        textEncoder.encode("\x1B\x61\x00Efectivo: "),
        textEncoder.encode("\x1B\x61\x02$" + (cabecero.totalefectivo || 0).toFixed(2) + "\n"),
        textEncoder.encode("\x1B\x61\x00Cambio: "),
        textEncoder.encode("\x1B\x61\x02$" + (cabecero.totalcambio || 0).toFixed(2) + "\n")
    );

    return partes;
};

export const concatenarPartes = (partes) => {
    const totalLength = partes.reduce((sum, part) => sum + part.length, 0);
    const finalTicket = new Uint8Array(totalLength);
    let offset = 0;
    partes.forEach((part) => {
        finalTicket.set(part, offset);
        offset += part.length;
    });
    return finalTicket;
};

